import { isCancel } from "axios";
import type Rollbar from "rollbar";

import { isWaffleFlagEnabled } from "./waffle-flags";

type DebugLogRateLimitState = {
  windowStartMs: number;
  countInWindow: number;
  blockedUntilMs: number;
};

const DEBUG_LOG_MAX_PER_SECOND = 10;
const debugLogRateLimitByMessageKey = new Map<string, DebugLogRateLimitState>();

type ObjectWithConstructor = { constructor: { name: string } };

function getDebugLogMessageKey(flag: string, args: unknown[]): string {
  const first = args[0];
  if (typeof first === "string") {
    // Most call sites pass a stable message string first.
    return `${flag}::${first}`;
  }

  if (first instanceof Error) {
    return `${flag}::Error(${first.name}):${first.message}`;
  }

  if (first && typeof first === "object") {
    const firstAsObj = first as ObjectWithConstructor;
    const ctorName =
      firstAsObj.constructor && typeof firstAsObj.constructor.name === "string"
        ? firstAsObj.constructor.name
        : "object";
    return `${flag}::${ctorName}`;
  }
  return `${flag}::${String(first)}`;
}

// In practice this function is intended to take strings or error objects (of various types). Once we start using Rollbar actively
// we should investigate both how the error is logged and if we can improve the typing.
export function rollbarAndLogError(...args: unknown[]) {
  // Don't log canceled requests - these are expected when user is inactive
  console.error(...args);
  if (args.some((arg) => isCancel(arg))) {
    return;
  }

  if (window.Rollbar) {
    window.Rollbar.error(...(args as Rollbar.LogArgument[]));
  }
}

export function rollbarAndLogWarning(...args: unknown[]) {
  console.warn(...args);
  if (window.Rollbar) {
    window.Rollbar.warn(...(args as Rollbar.LogArgument[]));
  }
}

export function rollbarAndLogInfo(...args: unknown[]) {
  console.info(...args);
  if (window.Rollbar) {
    window.Rollbar.info(...(args as Rollbar.LogArgument[]));
  }
}

export function logInfoWithDebugFlag(flag: string, ...args: unknown[]) {
  if (isWaffleFlagEnabled(flag)) {
    const now = Date.now();
    const key = getDebugLogMessageKey(flag, args);
    const existing = debugLogRateLimitByMessageKey.get(key);
    const state: DebugLogRateLimitState =
      existing ??
      ({
        windowStartMs: now,
        countInWindow: 0,
        blockedUntilMs: 0,
      } satisfies DebugLogRateLimitState);

    if (state.blockedUntilMs > 0 && now < state.blockedUntilMs) {
      debugLogRateLimitByMessageKey.set(key, state);
      return;
    }

    if (now - state.windowStartMs >= 1000) {
      state.windowStartMs = now;
      state.countInWindow = 0;
      state.blockedUntilMs = 0;
    }

    // Ensure we log at most `maxPerSecond` messages per second per message.
    // When we hit the cap, the last allowed log is prefixed, and then logging is suppressed for ~1s.
    if (state.countInWindow >= DEBUG_LOG_MAX_PER_SECOND - 1) {
      console.info(`[${flag}] [Max: ${DEBUG_LOG_MAX_PER_SECOND} per second reached]`, ...args);
      state.countInWindow = DEBUG_LOG_MAX_PER_SECOND;
      state.blockedUntilMs = now + 1000;
      debugLogRateLimitByMessageKey.set(key, state);
      return;
    }

    state.countInWindow += 1;
    debugLogRateLimitByMessageKey.set(key, state);
    console.info(`[${flag}]`, ...args);
  }
}
